import { request } from "../../api";

import type { URLPoolKind, URLVulnStatus } from "./urlPool";
import type {
  BlockReasonCode,
  NetworkEventAnalysis,
  NetworkEventBlockReason,
  NetworkEventMatchedRule,
  NetworkPolicySummary,
} from "./events";

export interface NetworkViolationRow {
  id: string;
  device_id: string;
  device_uid: string;
  attempted_at: string;
  destination: string;
  destination_type: string;
  pool_kind: string;
  resolved_ip: string;
  protocol: string;
  port: number | null;
  protection_policy_mode: string;
  matched_rule_id: string | null;
  policy_version: number;
  policy_hash: string | null;
  bytes_denied: number;
  url_pool_vuln_status: URLVulnStatus;
  policy_name?: string | null;
  policy_default_action?: string | null;
  block_reason_code?: BlockReasonCode | null;
  block_reason_label?: string | null;
  block_reason_source?: string | null;
  /** Legacy aliases kept during transition */
  occurred_at: string;
  url_pool_value: string;
  url_pool_kind: URLPoolKind | string;
  verdict: "blocked";
}

export interface NetworkViolationsPage {
  total: number;
  limit: number;
  offset: number;
  /**
   * Days of raw per-attempt rows kept before purge. Rows older than this live
   * only in the aggregated Insights tabs (Destinations / Devices /
   * Timeline), which are backed by the daily rollup. Used to flag
   * beyond-retention ranges on the raw-events list + drill-down.
   */
  raw_retention_days?: number;
  items: NetworkViolationRow[];
}

export interface NetworkViolationsFilters {
  device?: string;
  device_uid?: string;
  search?: string;
  q?: string;
  destination_type?: string;
  kind?: URLPoolKind | string;
  matched_rule_id?: string;
  policy_version?: number;
  from?: string;
  to?: string;
  device_group?: string;
  platform?: "android" | "ios" | string;
  limit?: number;
  offset?: number;
}

export type ViolationsGroupByDimension = "destination" | "device" | "rule";

export interface ViolationsScope {
  from?: string;
  to?: string;
  device?: string;
  device_group?: string;
  platform?: "android" | "ios" | string;
}

export interface DestinationBucket {
  key: string;
  label: string;
  destination_type: string;
  total_attempts: number;
  distinct_devices: number;
  first_attempted_at: string;
  last_attempted_at: string;
}

export interface DeviceBucket {
  key: string;
  label: string;
  device_uid: string;
  platform: string;
  model: string;
  total_attempts: number;
  distinct_destinations: number;
  first_attempted_at: string;
  last_attempted_at: string;
}

export interface RuleBucket {
  key: string | null;
  label: string;
  total_attempts: number;
  distinct_destinations: number;
  distinct_devices: number;
  first_attempted_at: string;
  last_attempted_at: string;
}

export interface GroupByResponse<T> {
  dimension: ViolationsGroupByDimension;
  total_groups: number;
  limit: number;
  items: T[];
}

export interface TimelinePoint {
  ts: string;
  count: number;
}

export interface TimelineResponse {
  bucket: "hour" | "day";
  from: string;
  to: string;
  series: TimelinePoint[];
}

export interface NetworkViolationDetail {
  violation: NetworkViolationRow;
  event: NetworkViolationRow;
  analysis: NetworkEventAnalysis;
}

function qs(params: object): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params) as [string, unknown][]) {
    if (v === undefined || v === "" || v === null) continue;
    sp.append(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export type {
  BlockReasonCode,
  NetworkEventAnalysis,
  NetworkEventBlockReason,
  NetworkEventMatchedRule,
  NetworkPolicySummary,
};

export const networkViolationsApi = {
  list: (filters: NetworkViolationsFilters = {}) =>
    request<NetworkViolationsPage>(
      `/api/admin/v1/monitoring/network-violations/${qs(filters)}`,
    ),

  get: (violationId: string) =>
    request<NetworkViolationDetail>(
      `/api/admin/v1/monitoring/network-violations/${violationId}/`,
    ),

  groupByDestinations: (scope: ViolationsScope = {}, limit?: number) =>
    request<GroupByResponse<DestinationBucket>>(
      `/api/admin/v1/monitoring/violations/group-by/${qs({ ...scope, dimension: "destination", limit })}`,
    ),

  groupByDevices: (scope: ViolationsScope = {}, limit?: number) =>
    request<GroupByResponse<DeviceBucket>>(
      `/api/admin/v1/monitoring/violations/group-by/${qs({ ...scope, dimension: "device", limit })}`,
    ),

  groupByRules: (scope: ViolationsScope = {}, limit?: number) =>
    request<GroupByResponse<RuleBucket>>(
      `/api/admin/v1/monitoring/violations/group-by/${qs({ ...scope, dimension: "rule", limit })}`,
    ),

  timeline: (
    scope: ViolationsScope & { from: string; to: string },
    bucket: "hour" | "day" = "day",
  ) =>
    request<TimelineResponse>(
      `/api/admin/v1/monitoring/violations/timeline/${qs({ ...scope, bucket })}`,
    ),

  bulkGrant: (input: {
    violation_ids: string[];
    duration_hours: number | null;
    reason?: string;
  }) =>
    request<BulkGrantResponse>(`/api/admin/v1/monitoring/violations/bulk-grant/`, {
      method: "POST",
      json: input,
    }),
};

export interface BulkGrantSucceededRow {
  violation_id: string;
  device_id: string;
  destination: string;
  grant_id: string;
  expires_at: string | null;
}
export interface BulkGrantSkippedRow {
  violation_id: string;
  device_id: string;
  destination: string;
  grant_id: string;
}
export interface BulkGrantFailedRow {
  violation_id: string;
  device_id: string;
  destination: string;
  error: string;
}
export interface BulkGrantResponse {
  requested: number;
  matched: number;
  max_per_call: number;
  succeeded: BulkGrantSucceededRow[];
  skipped: BulkGrantSkippedRow[];
  failed: BulkGrantFailedRow[];
}
